import { useMutation, useQuery } from "@tanstack/react-query";
import { useAdminApplication, useAdminSession } from "../../app/providers";
import {
  decideBenefitApproval,
  getBenefitDetail,
  getBenefits,
  recordManualBenefitPayout,
  rejectBenefitClaim,
  requestBenefitApproval,
} from "./api";
import type { BenefitListQuery } from "./api";
import type { Schemas } from "../workspace/api.generated";

export const benefitKeys = {
  all: (sessionEpoch: number) => ["admin", sessionEpoch, "benefits"] as const,
  lists: (sessionEpoch: number) => [...benefitKeys.all(sessionEpoch), "list"] as const,
  list: (sessionEpoch: number, query: BenefitListQuery) => (
    [...benefitKeys.lists(sessionEpoch), query] as const
  ),
  detail: (sessionEpoch: number, claimId: number) => (
    [...benefitKeys.all(sessionEpoch), "detail", claimId] as const
  ),
};

export function useBenefitList(query: BenefitListQuery) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: benefitKeys.list(session.sessionEpoch, query),
    queryFn: ({ signal }) => getBenefits(apiClient, query, signal),
    refetchOnMount: "always",
    staleTime: 0,
  });
}

export function useBenefitDetail(claimId?: number) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: benefitKeys.detail(session.sessionEpoch, claimId ?? 0),
    queryFn: ({ signal }) => getBenefitDetail(apiClient, claimId!, signal),
    enabled: claimId !== undefined,
    staleTime: 0,
  });
}

type BenefitMutation =
  | { readonly kind: "request"; readonly claimId: number; readonly body: Schemas["RequestBenefitApprovalRequest"] }
  | { readonly kind: "claim-reject"; readonly claimId: number; readonly body: Schemas["BenefitReasonRequest"] }
  | { readonly kind: "decision"; readonly claimId: number; readonly approvalId: number; readonly body: Schemas["DecideBenefitApprovalRequest"] }
  | { readonly kind: "payout"; readonly claimId: number; readonly body: Schemas["RecordManualBenefitPayoutRequest"] };

export function useBenefitMutation() {
  const { apiClient, queryClient, sessionLifecycle } = useAdminApplication();
  const session = useAdminSession();
  return useMutation({
    mutationFn: async (input: BenefitMutation) => {
      const request = sessionLifecycle.createRequestController();
      try {
        switch (input.kind) {
          case "request":
            return await requestBenefitApproval(apiClient, input.claimId, input.body, request.controller.signal);
          case "claim-reject":
            return await rejectBenefitClaim(apiClient, input.claimId, input.body, request.controller.signal);
          case "decision":
            return await decideBenefitApproval(apiClient, input.approvalId, input.body, request.controller.signal);
          case "payout":
            return await recordManualBenefitPayout(apiClient, input.claimId, input.body, request.controller.signal);
        }
      } finally {
        request.release();
      }
    },
    onSuccess: async (_result, input) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: benefitKeys.lists(session.sessionEpoch), refetchType: "none" }),
        queryClient.invalidateQueries({ queryKey: benefitKeys.detail(session.sessionEpoch, input.claimId), refetchType: "none" }),
      ]);
    },
  });
}

